<script context="module" lang="ts">
  const instances = new Set<{ close: () => void }>();
</script>

<script lang="ts">
  // popper references: process.env...
  if (!globalThis.process) Object.assign(globalThis, { process: { env: {} } });

  import { createPopper, preventOverflow, flip } from "@popperjs/core";
  import type { Instance } from "@popperjs/core";
  import { createEventDispatcher } from "svelte";
  import { onDestroy, onMount } from "svelte";
  export let open: boolean;
  export let input: HTMLElement;
  export let closeLabel: string;
  // TODO maybe add an option for popper placement strategy fixed

  const dispatcher = createEventDispatcher();

  let modal: HTMLDivElement;
  let popper: Instance;
  let ready = false;
  const inst = { close, isOpen: false, resize };

  $: {
    // prevent instant close with open click!
    if (inst.isOpen !== open) {
      inst.isOpen = open;
      setTimeout(() => (ready = inst.isOpen), 10);
    }

    if (open) {
      popper.update();
      closeOthers();
    }
  }

  let parent: Element;
  onMount(() => {
    parent = modal.parentElement;
    document.body.append(modal);
    popper = createPopper(input, modal, {
      placement: "bottom-start",
      modifiers: [preventOverflow, flip],
    });

    instances.add(inst);
    return () => instances.delete(inst);
  });

  onDestroy(() => {
    popper.destroy();
    parent.append(modal);
  });

  function handleKeydown(e: KeyboardEvent) {
    if (e.key === "Escape") close();
  }

  function handleBodyClick(e: MouseEvent) {
    if (open && ready) {
      const node = e.target as Node;
      if (isMounted(node) && !modal.contains(node) && !input.contains(node)) {
        close();
      }
    }
  }

  function close() {
    if (open) {
      dispatcher("close");
      open = false;
    }
  }

  function closeOthers() {
    instances.forEach((_) => {
      if (_ !== inst) _.close();
    });
  }

  function isMounted(node: Node) {
    if (node.nodeType === Node.DOCUMENT_NODE) return true;
    if (node.parentNode == undefined) return false;
    return isMounted(node.parentNode);
  }

  function resize() {
    popper?.update();
  }
</script>

<svelte:window on:keydown={handleKeydown} />
<svelte:body on:click={handleBodyClick} />

<div bind:this={modal} class="lrs-datepicker-zindex">
  {#if open}
    <div class="lrs-popper">
      <slot methods={inst} />
      <div class="lrs-popper-actions">
        <button class="bs3-btn bs3-btn-primary lrs-popper-close" on:click={(_) => close()}>{closeLabel}</button>
      </div>
    </div>
  {/if}
</div>

<style lang="scss" global>
  .lrs-popper {
    position: relative;
    max-width: calc(100% - 1em);
    max-height: calc(100% - 1em);
    box-sizing: content-box;
    border-radius: 28px;
    padding: 0.5rem;
    background-color: #fff;
    box-shadow: 0px 2px 35px -2px rgb(0 0 0 / 15%);
  }

  .lrs-popper-actions {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 1rem;
    padding: 0.5rem;
  }

  :where(.lrs-popper-close) {
    display: inline-block;
    text-align: center;
    vertical-align: middle;
    cursor: pointer;
    user-select: none;
    background-color: #ffbc00;
    border: 1px solid #ffbc00;
    color: #000;
    padding: 0.5rem 1.5rem;
    font-size: 1rem;
    border-radius: 9999px;

    &:where(:hover) {
      background-color: #d9a000;
      border-color: #cc9600;
    }

    &:where(&:focus) {
      background-color: #d9a000;
      border-color: #000;
      box-shadow: inset 0 1px 1px rgb(0 0 0 / 8%), 0 0 0 1px #000;
    }

    &:where(&:active) {
      background-color: #cc9600;
      border-color: #bf8d00;
    }
  }
</style>
